/**
 * Black Tulip v3 — fit engine
 *
 * The size answer, in one place. Read by the PDP drawer and by the
 * Find Your Size page, so both can never disagree.
 *
 * Rules locked 2026-09-04 (docs/measurement-lock.md), first rule replaced
 * 2026-09-17 (docs/phase7/08-sizing-rule-challenge.md):
 *   · Return the size whose room is CLOSEST to the style's toe allowance, among
 *     sizes no more than 3 mm tighter than it. This replaced "the smallest size
 *     clearing foot + allowance" — see NEAREST below.
 *   · When that size leaves less room than the style builds in, still return
 *     it. The next size up is offered as advice, never substituted — she is
 *     told it fits and told what the other one gives her. The threshold that
 *     counts as "close" moves with the rule; see TIGHT_MM.
 *   · Beyond the range, state the longest foot the style takes, whole mm.
 *   · Sizes are matched by NAME, never by list position, so a product that is
 *     not made in every size still reads the right row.
 *   · Chart figures keep one decimal, exactly as the factory supplied them.
 *
 * No dependencies. No build step. Attaches to window.BTFit.
 */
(function (root) {
  'use strict';

  /*
   * "Tight" means a different thing under each rule, and the difference is not
   * a preference — the approved copy names the threshold.
   *
   * CEILING could only ever leave MORE room than the style builds in, so tight
   * meant "barely clears it": spare under 3 mm. Ruling 1, 4 September.
   *
   * NEAREST chooses the size closest to the allowance, so a spare under 3 mm is
   * the ORDINARY case, not a warning — measured across the six styles over the
   * whole plausible foot range it fires on 81% of answers, against 47% before.
   * An advice line on four answers in five is furniture, and it would also
   * contradict v3.fys.between_body, which says the finder speaks up "where the
   * nearest size leaves LESS than that" — less than the style's own allowance,
   * which is spare below zero, not below three.
   *
   * So the threshold follows the rule. Under NEAREST the line fires on the 33%
   * of answers where she is getting less room than the style intends and there
   * is a larger size to offer, which is the case NEAREST introduces and CEILING
   * could not produce.
   */
  var TIGHT_MM = 3;
  var TIGHT_SPARE_MM = 0;

  /*
   * NEAREST — the selection rule. Sumit turned it on 17 September 2026.
   *
   * WHAT IT REPLACED, AND WHY.
   *
   * "Smallest size clearing foot + allowance" is a CEILING rule on a grid that
   * steps about 6.6 mm. It can never leave less room than the allowance and may
   * leave a full size more, so it recommends large — which is what Yash measured
   * on 15 September with a 252 mm foot on someone who wears UK 5 or 6. And at the
   * top of the range, with no larger size to round up to, it refuses a fit it
   * has: a 264 mm foot was told nothing fits in Willow, whose 8/41 leaves 6.5 mm
   * against an 8 mm allowance — 1.5 mm short, and rejected outright.
   *
   * NEAREST picks the size whose room is CLOSEST to the style's own allowance,
   * among sizes at most FLOOR_SPARE_MM tighter than it. Same measurements, same
   * allowances, different arithmetic.
   *
   * WHAT HAD TO LAND FIRST, and what it costs to reverse.
   *
   * Under a nearest rule `spare` goes NEGATIVE — the chosen size can have less
   * room than the style builds in, which is the whole point of it — and five
   * approved strings assumed it never does. Turning this on before they were
   * rewritten would have put a sentence on the page that the page then
   * contradicts, and printed "only -2.5 mm", which is not English. All five are
   * rewritten and all five are APPROVED — three Sumit was asked for, and two more
   * that turned up mid-build and were put to him separately rather than folded
   * in quietly. Nothing customer-visible in this change is unapproved copy:
   *
   *   v3.tab.sizefit_intro     describes the rule on the PDP
   *   v3.fys.between_body      describes it at length on Find Your Size
   *   v3.finder.tight_advice   the drawer's advice line, now in room
   *   v3.finder.internal_room  the drawer's meta line, was internal_spare
   *   (find-your-size.js STR)  the results grid's own line, now in room
   *
   * So the constant is not a free switch any more: flipping it back to false
   * puts those five sentences out of step with the arithmetic in the other
   * direction. Both rules stay implemented and both stay tested because
   * docs/ci/sizing-rules-compare.js compares them, not because a rollback is
   * one keystroke.
   *
   * ROOM IS THE UNIT THE PAGE SPEAKS IN. spare is still returned — the tight
   * test and the floor are both measured in it — but it is an internal
   * quantity now, and no surface prints it.
   */
  var NEAREST = true;

  /*
   * The floor, and why it is measured in SPARE rather than in room.
   *
   * A nearest rule needs a floor or it will hand a 270 mm foot a Willow 8/41
   * with 0.5 mm of room. The obvious floor is "at least 3 mm of room", and it is
   * wrong: room is measured from the foot, and it ignores that each style builds
   * in a different allowance for length no foot can reach. Iris allows 20 mm
   * because its toe is pointed. Under a 3 mm ROOM floor Iris would answer for a
   * foot of 283 mm — 3.4 mm of room against a 20 mm allowance, a claim the last
   * cannot honour. Every other style over-claims by the same mechanism, by 5 mm.
   *
   * Measured in spare, the floor scales with the style automatically: at most
   * 3 mm tighter than that style builds in. Iris then answers up to 269 mm and
   * Arbi up to 263 mm, which are both defensible. 3 is the same number ruling 1
   * already uses, in the same unit.
   */
  var FLOOR_SPARE_MM = -3;

  /** Round for display without inventing precision. */
  function oneDp(n) { return (Math.round(n * 10) / 10).toFixed(1); }
  function whole(n) { return String(Math.round(n)); }

  /**
   * @param {Object} style
   *   {string[]} sizes            e.g. ["3/36","4/37",…] — the spec.size_scale
   *   {number[]} lengths          spec.internal_length_mm, index-aligned to sizes
   *   {number}   toe              spec.toe_allowance_mm
   *   {string}   name             spec.style_name
   *   {string[]} [availableSizes] size values the product actually sells
   * @param {number} footMm
   */
  function answer(style, footMm) {
    if (!style || !style.sizes || !style.lengths) return null;
    if (style.sizes.length !== style.lengths.length) return null;
    if (typeof footMm !== 'number' || !isFinite(footMm)) return null;

    var toe = typeof style.toe === 'number' ? style.toe : 0;
    var target = footMm + toe;

    // CEILING — the rule locked on 4 September. The smallest size that clears
    // foot + allowance.
    var chosen = -1;
    if (!NEAREST) {
      for (var i = 0; i < style.lengths.length; i++) {
        if (style.lengths[i] >= target) { chosen = i; break; }
      }
    } else {
      // NEAREST — the size whose room is closest to the style's own allowance,
      // among sizes no more than FLOOR_SPARE_MM tighter than it. Ties go to the
      // ROOMIER size: on a six-size grid an exact tie means the foot sits
      // exactly between two sizes, and of the two mistakes available, half a
      // size of room is recoverable with a thicker sock and half a size short
      // is not.
      var best = Infinity;
      for (var j = 0; j < style.lengths.length; j++) {
        var sp = style.lengths[j] - target;
        if (sp < FLOOR_SPARE_MM) continue;
        var d = Math.abs(sp);
        if (d < best - 1e-9) { best = d; chosen = j; }
      }
    }

    if (chosen !== -1) {
      var spare = style.lengths[chosen] - target;
      // Under CEILING spare is never negative and "tight" means barely enough.
      // Under NEAREST it can be, and a size with LESS room than the style builds
      // in is exactly what the shopper needs telling about — which is why the
      // threshold moves with the rule rather than staying at 3. See TIGHT_MM.
      var tight = spare < (NEAREST ? TIGHT_SPARE_MM : TIGHT_MM);
      var larger = chosen + 1 < style.sizes.length ? style.sizes[chosen + 1] : null;
      return {
        beyondRange: false,
        size: style.sizes[chosen],
        sizeIndex: chosen,
        internalMm: oneDp(style.lengths[chosen]),
        spareMm: oneDp(spare),
        // Room is measured from the FOOT and is never negative for an answered
        // size. spareMm is measured from the allowance and can be, under
        // NEAREST. Both are reported so a surface can choose the one its
        // sentence can actually say.
        roomMm: oneDp(style.lengths[chosen] - footMm),
        toeMm: oneDp(toe),
        tight: tight,
        // Advice only. The answer above is the size that fits.
        adviceSize: tight && larger ? larger : null,
        adviceSpareMm: tight && larger ? oneDp(style.lengths[chosen + 1] - target) : null,
        adviceRoomMm: tight && larger ? oneDp(style.lengths[chosen + 1] - footMm) : null,
        offered: isOffered(style, style.sizes[chosen]),
        available: isAvailable(style, style.sizes[chosen])
      };
    }

    // The longest foot the style answers for, which is a property of the
    // SELECTION RULE and not only of the last. Under CEILING that is the
    // largest size minus the whole allowance; under NEAREST the floor buys back
    // FLOOR_SPARE_MM of it. Derived rather than written down twice.
    var last = style.lengths[style.lengths.length - 1];
    var maxUsable = NEAREST ? (last - toe - FLOOR_SPARE_MM) : (last - toe);
    return {
      beyondRange: true,
      size: null,
      maxFootMm: whole(maxUsable),
      styleName: style.name || ''
    };
  }

  /**
   * Two different facts, and the difference is what the customer is told.
   *
   * offered   this colourway has a variant for the size at all. Willow is
   *           measured in six sizes and sold in five — 3/36 is not made.
   * available that variant is in stock right now.
   *
   * A missing key means the page did not say, so nothing is claimed. An EMPTY
   * list means the page said "none" — which is not the same as saying nothing,
   * and the earlier `!length` short-circuit read the two as identical.
   */
  function isOffered(style, sizeName) {
    if (!style.offeredSizes) return true;
    return style.offeredSizes.indexOf(sizeName) !== -1;
  }
  function isAvailable(style, sizeName) {
    if (!style.availableSizes) return true;
    return style.availableSizes.indexOf(sizeName) !== -1;
  }

  /** Row lookup by size NAME. Never by index — see ruling 4. */
  function rowForSize(style, sizeName) {
    if (!style || !style.sizes) return null;
    var i = style.sizes.indexOf(sizeName);
    if (i === -1) return null;
    return {
      size: sizeName,
      internalMm: oneDp(style.lengths[i]),
      usableMm: oneDp(style.lengths[i] - (style.toe || 0)),
      heelMm: style.heels && style.heels[i] != null ? String(style.heels[i]) : null,
      backHeightMm: style.backHeights && style.backHeights[i] != null ? String(style.backHeights[i]) : null,
      calfTopMm: style.calfTop && style.calfTop[i] != null ? String(style.calfTop[i]) : null,
      calfMidMm: style.calfMid && style.calfMid[i] != null ? String(style.calfMid[i]) : null,
      openingMm: style.opening && style.opening[i] != null ? String(style.opening[i]) : null
    };
  }

  /** Every row, for the chart. */
  function chart(style) {
    if (!style || !style.sizes) return [];
    return style.sizes.map(function (s) { return rowForSize(style, s); });
  }

  /**
   * Find Your Size flags a style sitting two or more steps from the median
   * answer across all styles at the same foot length.
   */
  function medianIndex(answers) {
    var idx = answers
      .filter(function (a) { return a && !a.beyondRange; })
      .map(function (a) { return a.sizeIndex; })
      .sort(function (a, b) { return a - b; });
    if (!idx.length) return null;
    return idx[Math.floor(idx.length / 2)];
  }

  function outliers(answers) {
    var med = medianIndex(answers);
    if (med === null) return [];
    return answers.map(function (a) {
      if (!a || a.beyondRange) return 0;
      return Math.abs(a.sizeIndex - med);
    });
  }

  root.BTFit = {
    answer: answer,
    rowForSize: rowForSize,
    chart: chart,
    medianIndex: medianIndex,
    outliers: outliers,
    TIGHT_MM: TIGHT_MM,
    TIGHT_SPARE_MM: TIGHT_SPARE_MM,
    // Exposed so the comparison script and the tests can exercise both rules
    // without a second copy of the arithmetic living somewhere else.
    NEAREST: NEAREST,
    FLOOR_SPARE_MM: FLOOR_SPARE_MM,
    _setNearest: function (on) { NEAREST = !!on; }
  };
})(typeof window !== 'undefined' ? window : globalThis);

if (typeof module !== 'undefined' && module.exports) {
  module.exports = (typeof window !== 'undefined' ? window : globalThis).BTFit;
}
